import { db, database, storage, BUCKET_NAME } from '@/config/firebase';
import { doc, getDoc, setDoc, updateDoc, Timestamp, getDocs, collection, query, where, deleteDoc, writeBatch, orderBy, limit, startAfter, getCountFromServer } from 'firebase/firestore';
import { ref, get, update } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL, uploadBytesResumable, listAll, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { deleteUser, revokeUser } from './api';

/* Global */
export async function getDocData(collectionName, docId) {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      console.log(`No Document with ID "${docId}" in collection "${collectionName}".`);
      return null;
    }

    return docSnapshot.data();
  } catch (error) {
    console.error('Error get document:', error);
    return;
  }
}

export async function setDocData(collectionName, docId, docData) {
  try {
    const docRef = doc(db, collectionName, docId);
    await setDoc(docRef, docData);
  } catch (error) {
    console.error('Error set document:', error);
  }
}

export async function updateDocData(collectionName, docId, updateDocObj) {
  try {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, updateDocObj);
  } catch (error) {
    console.error('Error update document:', error);
  }
}

export async function deleteDocData(collectionName, docId) {
  try {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting document:', error);
  }
}

export async function getAllDocs(collectionName) {
  try {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error all documents:', error);
    return [];
  }
}

export async function getDocByValue(collectionName, field, value, operator = "==") {
  const queryRef = collection(db, collectionName);
  const querySnapshot = await getDocs(query(queryRef, where(field, operator, value)));

  if (querySnapshot.empty) {
    return null;
  } else {
    return querySnapshot.docs[0].data();
  }
}

export async function uploadFileStorage(collection, id, file, filename = 'profile.jpg') {
  const fileRef = storageRef(storage, `${collection}/${id}/${filename}`);
  await uploadBytes(fileRef, file);
  return await getDownloadURL(fileRef);
}

/* Sessions */
export async function getUserSongs(songIds = []) {
  const q = query(
    collection(db, 'songs'),
    where('jobId', 'in', songIds),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);

  const songsWithComments = await Promise.all(
    querySnapshot.docs.map(async (doc) => {
      const data = doc.data();
      const commentCount = await countCommentsBySongId(doc.id);
      return {
        id: doc.id,
        ...data,
        commentCount,
      };
    })
  );

  return songsWithComments;
}

export async function getUserSessions(userId) {
  const q = query(
    collection(db, 'sessions'),
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc'),
    orderBy('sessionName', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data()).filter((doc) => doc.autosplit !== true);
}

export async function getUserSessionById(userId, sessionId) {
  const userData = await getDocData('profiles', userId);
  if (!userData) {
    return null;
  }
  const sessionData = await getDocData('sessions', sessionId);
  if (!sessionData) {
    return null;
  }
  const result = await Promise.all(
    sessionData?.songs?.map(async (songId) => {
      const song = await getDocData('songs', songId);
      if (!song) return null;
      const commentCount = await countCommentsBySongId(songId);
      return {
        ...song,
        commentCount,
      };
    })
  );
  sessionData.songs = result.filter((e) => !!e);
  return {
    owner: userData,
    session: sessionData
  }
}

export async function countCommentsBySongId(songId) {
  try {
    const q = query(
      collection(db, 'comments'),
      where('songId', '==', songId),
      orderBy('createdAt', 'desc'),
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error counting documents:", error);
    throw error;
  }
}

export async function getUserSongById(userId, songId) {
  const userData = await getDocData('profiles', userId);
  if (!userData) {
    return null;
  }
  const songData = await getDocData('songs', songId);
  if (!songData) {
    return null;
  }
  const countComments = await countCommentsBySongId(songId);

  return {
    owner: userData,
    song: { ...songData, countComments }
  }
}

export async function pushSongToSession(sessionId, songId) {
  const sessionData = await getDocData('sessions', sessionId);
  const songs = [songId, ...sessionData?.songs || []];
  await updateDocData('sessions', sessionId, { songs });
}

export async function pushSessionToUser(userId, sessionId) {
  const userData = await getDocData('users', userId);
  const sessions = [sessionId, ...userData?.sessions || []];
  await updateDocData('users', userId, { sessions });
}

export async function deleteSessionFromUser(userId, sessionId) {
  const userData = await getDocData('users', userId);
  const sessions = (userData?.sessions || []).filter((sesId) => sesId !== sessionId);

  const sessionData = await getDocData('sessions', sessionId);
  await Promise.all(
    (sessionData?.songs || []).map(async (songId) => await deleteFeedsWithSongId(songId))
  );
  await updateDocData('users', userId, { sessions });
}

export async function deleteSongFromSession(sessionId, songId) {
  const sessionData = await getDocData('sessions', sessionId);
  const songs = (sessionData?.songs || []).filter((sonId) => sonId !== songId);
  await deleteFeedsWithSongId(songId);
  await updateDocData('sessions', sessionId, { songs });
}

export async function deleteFeedsWithSongId(songId) {
  try {
    // Query the `feed` collection for documents with the specified songId
    const feedCollection = collection(db, "feed");
    const q = query(feedCollection, where("songId", "==", songId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(`No feeds found with songId: ${songId}`);
      return;
    }

    // Delete each document in the query result
    const deletePromises = querySnapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, "feed", docSnap.id))
    );

    await Promise.all(deletePromises);

    console.log(`All feeds with songId: ${songId} have been deleted.`);
  } catch (error) {
    console.error("Error deleting feeds with songId:", error);
  }
}

export async function getSongWithProfile(songId) {
  const songData = await getDocData('songs', songId);
  if (!songData) return null;
  const profileData = await getDocData('profiles', songData.ownerId);
  const countComments = await countCommentsBySongId(songId);
  return {
    ...songData,
    owner: profileData,
    countComments,
  }
}

export async function getUserActiveSongs(userId) {
  const q = query(
    collection(db, 'songs'),
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc'),
    orderBy('songName', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data());
}

/* Profiles */
export async function getExistingUsernames(userId) {
  try {
    const querySnapshot = await getDocs(collection(db, "profiles"));
    return querySnapshot.docs.filter((doc) => doc.id !== userId).map(doc => doc.data().username);
  } catch (error) {
    console.error("Error fetching profiles collection data:", error);
    return [];
  }
}

export async function getProfileDocDataByusername(username) {
  const profilesRef = collection(db, "profiles");
  const querySnapshot = await getDocs(query(profilesRef, where("username", "==", username)));

  if (querySnapshot.empty) {
    return null;
  } else {
    const profileDoc = querySnapshot.docs[0];
    return profileDoc.data();
  }
}

export async function addProfileSong(userId, songId) {
  const profileData = await getDocData('profiles', userId);
  if (profileData?.songs?.includes(songId)) {
    return;
  }
  const songs = [songId, ...(profileData?.songs || [])];
  await updateDocData('profiles', userId, { songs, updatedAt: Timestamp.now() });
  if (!profileData.private) {
    await setDocData('feed', uuidv4(), {
      songId,
      targetId: userId,
      createdAt: new Date()
    });
  }
}

export async function pushImageToProfile(userId, image) {
  const profileData = await getDocData('profiles', userId);
  const images = [image, ...profileData?.images || []];
  await updateDocData('profiles', userId, { images, updatedAt: Timestamp.now() });
}

export async function deleteImageFromProfile(userId, imageId) {
  const profileData = await getDocData('profiles', userId);
  const images = (profileData?.images || []).filter((image) => image.id !== imageId);
  await updateDocData('profiles', userId, { images, updatedAt: Timestamp.now() });
}

export async function getVideosByIds(videoIds = []) {
  if (!videoIds.length) {
    return [];
  }
  const q = query(
    collection(db, 'videos'),
    where('jobId', 'in', videoIds)
  );

  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data());
}

/* Job Ststus */
export async function deleteJobStatus(userId, jobId) {
  const jobStatusRef = ref(database, `sessions/${userId}`);

  try {
    const snapshot = await get(jobStatusRef);
    if (!snapshot.exists()) {
      console.log("No job status");
      return;
    }

    const jobStatus = snapshot.val();
    delete jobStatus[jobId];

    await update(jobStatusRef, jobStatus);
    console.log("job status removed:", jobId, jobStatus[jobId]);

  } catch (error) {
    console.error("Error delete job status:", error);
    throw error;
  }
}

export async function updateJobStatus(userId, jobId, newStatus) {
  const jobStatusRef = ref(database, `sessions/${userId}`);

  try {
    await update(jobStatusRef, {
      [jobId]: newStatus
    });
    console.log("job status updated:", jobId);
  } catch (error) {
    console.error("Error delete job status:", error);
    throw error;
  }
}

/* Groups */
export async function getUserActiveGroups(userId) {
  const groupsRef = collection(db, 'groups');
  const groups = [];

  try {
    const adminQuery = query(groupsRef, where('admin', 'array-contains', userId));
    const adminQuerySnapshot = await getDocs(adminQuery);

    if (!adminQuerySnapshot.empty) {
      adminQuerySnapshot.forEach((doc) => {
        groups.push({
          id: doc.id,
          ...doc.data(),
        });
      });
    }

    const membersQuery = query(groupsRef, where('members', 'array-contains', userId));
    const membersQuerySnapshot = await getDocs(membersQuery);

    if (!membersQuerySnapshot.empty) {
      membersQuerySnapshot.forEach((doc) => {
        if (!groups.find((group) => group.id === doc.id)) {
          groups.push({
            id: doc.id,
            ...doc.data(),
          });
        }
      });
    }
  } catch (error) {
    console.log('Error getting groups: ', error);
  }
  return groups;
}

export async function getUserFollowingGroups(userId) {
  const groupsRef = collection(db, 'groups');
  const groups = [];

  try {
    const followersQuery = query(groupsRef, where('followers', 'array-contains', userId));
    const followersQuerySnapshot = await getDocs(followersQuery);

    if (!followersQuerySnapshot.empty) {
      followersQuerySnapshot.forEach((doc) => {
        if (!groups.find((group) => group.id === doc.id)) {
          groups.push({
            id: doc.id,
            ...doc.data(),
          });
        }
      });
    }
  } catch (error) {
    console.log('Error getting groups: ', error);
  }
  return groups;
}

export async function getExistingGroupNames(groupId) {
  try {
    const querySnapshot = await getDocs(collection(db, "groups"));
    return querySnapshot.docs.filter((doc) => doc.id !== groupId).map(doc => doc.data().name);
  } catch (error) {
    console.error("Error fetching groups collection data:", error);
    return [];
  }
}

export async function addGroupSong(groupId, songId, userId) {
  const groupData = await getDocData('groups', groupId);
  const songs = [songId, ...groupData?.songs?.filter((item) => item !== songId) || []];
  await updateDocData('groups', groupId, { songs });
  await setDocData('feed', uuidv4(), {
    songId,
    targetId: groupId,
    addedBy: userId,
    createdAt: new Date()
  });
}

export async function deleteGroupsByAdmin(adminId) {
  try {
    const userActiveGroups = await getUserActiveGroups(adminId);
    await Promise.all(
      userActiveGroups.map(async (group) => {
        if (group.admin && group.admin[0] === adminId) {
          return await deleteDocData('groups', group.id);
        }
        return await updateDocData('groups', group.id, {
          admin: group.admin?.filter((admId) => admId !== adminId) || [],
          members: group.members?.filter((memId) => memId !== adminId) || [],
        });
      })
    );

    const userFollowingGroups = await getUserFollowingGroups(adminId);
    return await Promise.all(
      userFollowingGroups.map(async (group) => {
        return await updateDocData('groups', group.id, {
          followers: group.followers?.filter((folId) => folId !== adminId) || []
        });
      })
    );
  } catch (error) {
    console.error("Error deleting groups when delete admin:", error);
    throw error;
  }
}

/* Sets */
export async function getUserActiveSets(userId) {
  const q = query(
    collection(db, 'sets'),
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc'),
    orderBy('name', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data());
}

export async function pushSetToUser(userId, setId) {
  const userData = await getDocData('users', userId);
  const sets = [setId, ...userData?.sets || []];
  await updateDocData('users', userId, { sets });
}

export async function getUserSetById(setId) {
  const setData = await getDocData('sets', setId);
  if (!setData || !setData.ownerId) {
    return null;
  }
  const userData = await getDocData('profiles', setData.ownerId);
  const result = await Promise.all(
    setData?.songs?.map(async (songId) => {
      const song = await getDocData('songs', songId);
      if (!song) return null;
      const commentCount = await countCommentsBySongId(songId);
      return {
        ...song,
        commentCount,
      };
    })
  );
  setData.songs = result.filter((e) => !!e);
  return {
    owner: userData,
    set: setData
  }
}

export async function addSetSong(setId, songId) {
  const setData = await getDocData('sets', setId);
  const songs = [songId, ...setData?.songs?.filter((item) => item !== songId) || []];
  await updateDocData('sets', setId, { songs });
}

/* Battles */
export async function getExistingBattleNames(battleId) {
  try {
    const querySnapshot = await getDocs(collection(db, "battles"));
    return querySnapshot.docs.filter((doc) => doc.id !== battleId).map(doc => doc.data().name);
  } catch (error) {
    console.error("Error fetching battles collection data:", error);
    return [];
  }
}

export async function getActiveBattles() {
  const now = Timestamp.now();
  const q = query(
    collection(db, 'battles'),
    where('startDate', '<=', now),
    where('endDate', '>=', now),
    orderBy('startDate', 'desc'),
    orderBy('name', 'asc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => doc.data());
}

export async function countActiveBattleDocuments() {
  try {
    const now = Timestamp.now();
    const q = query(
      collection(db, 'battles'),
      where('startDate', '<=', now),
      where('endDate', '>=', now),
      orderBy('startDate', 'desc'),
      orderBy('name', 'asc')
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error counting documents:", error);
    throw error;
  }
}

export async function addBattleSong(battleId, songId) {
  const battleData = await getDocData('battles', battleId);
  const songs = [songId, ...battleData?.songs?.filter((item) => item !== songId) || []];
  await updateDocData('battles', battleId, { songs });
}

export async function addVoteSong(battleId, userId, songId) {
  try {
    const battleData = await getDocData('battles', battleId);
    if (battleData.startDate?.seconds > Timestamp.now().seconds) {
      throw new Error("This battle has not started.");
    }
    if (battleData.endDate?.seconds <= Timestamp.now().seconds) {
      throw new Error("This battle has ended.");
    }
    const voted = battleData?.votes?.find((item) => item.userId === userId && item.songId === songId);
    const votes = [...battleData?.votes?.filter((item) => item.userId !== userId) || []];
    if (!voted) {
      votes.unshift({
        userId,
        songId,
        updatedAt: Timestamp.now(),
      });
    }
    await updateDocData('battles', battleId, { votes });
  } catch (error) {
    console.error("Error vote song:", error);
    throw error;
  }
}

/* Videos */
export async function getUserActiveVideos(userId) {
  const q = query(
    collection(db, 'videos'),
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc'),
    orderBy('name', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data());
}

export async function getActiveVideos() {
  const q = query(
    collection(db, 'videos'),
    orderBy('createdAt', 'desc'),
    orderBy('name', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data());
}

export async function pushSongToVideoTemplate(templateId, songId) {
  const templateData = await getDocData('video_templates', templateId);
  const songs = [songId, ...templateData?.songs || []];
  await updateDocData('video_templates', templateId, { songs });
  // const songData = await getDocData('songs', songId);
  // const templates = [templateId, ...songData?.templates || []];
  // await updateDocData('songs', songId, { templates });
}

export async function pullSongFromVideoTemplate(templateId, songId) {
  const templateData = await getDocData('video_templates', templateId);
  const songs = (templateData?.songs || []).filter((id) => id !== songId);
  await updateDocData('video_templates', templateId, { songs });
  // const songData = await getDocData('songs', songId);
  // const templates = (songData?.templates || []).filter((id) => id !== templateId);
  // await updateDocData('songs', songId, { templates });
}

export async function pushVideoToProfile(userId, video) {
  const profileData = await getDocData('profiles', userId);
  const videos = [video.jobId, ...profileData?.videos || []];
  await updateDocData('profiles', userId, { videos, updatedAt: Timestamp.now() });
}

export async function deleteVideoFromProfile(userId, videoId) {
  const profileData = await getDocData('profiles', userId);
  const videos = (profileData?.videos || []).filter((vidId) => vidId !== videoId);
  await updateDocData('profiles', userId, { videos, updatedAt: Timestamp.now() });
}

/* Comments */
export async function getCommentsWithProfileBySongId(songId) {
  const q = query(
    collection(db, 'comments'),
    where('songId', '==', songId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return await Promise.all(querySnapshot.docs.map(async (doc) => {
    const docData = doc.data();
    const profile = await getDocData('profiles', docData.userId);
    return {
      id: doc.id,
      ...docData,
      profile,
      comments: await Promise.all((docData.comments || []).map(async (item) => {
        const prof = await getDocData('profiles', item.userId);
        return {
          id: doc.id,
          ...item,
          profile: prof,
        }
      }))
    }
  }));
}

/* Feed */
export async function removeFeedFromProfile(songId, userId) {
  const feedRef = collection(db, 'feed');
  const q = query(feedRef, where('songId', '==', songId), where('targetId', '==', userId));

  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const batch = writeBatch(db);

    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } else {
    console.log('No documents found to delete.');
  }
}

export async function removeFeedFromGroup(songId, groupId) {
  const feedRef = collection(db, 'feed');
  const q = query(feedRef, where('songId', '==', songId), where('targetId', '==', groupId));

  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const batch = writeBatch(db);

    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } else {
    console.log('No documents found to delete.');
  }
}

export async function removeAllFeedFromProfile(userId) {
  const feedRef = collection(db, 'feed');
  const q = query(feedRef, where('targetId', '==', userId));

  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const batch = writeBatch(db);

    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } else {
    console.log('No documents found to delete.');
  }
}

export async function addAllFeedFromProfile(userId) {
  const profileData = await getDocData('profiles', userId);
  const batch = writeBatch(db);

  (profileData?.songs || []).forEach(songId => {
    const userDocRef = doc(db, 'feed', uuidv4());
    batch.set(userDocRef, {
      songId,
      targetId: userId,
      createdAt: new Date()
    });
  });

  await batch.commit();
}

export async function getAllFeedData(start = null) {
  try {
    let q = query(
      collection(db, 'feed'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    if (start) {
      q = query(q, startAfter(start));
    }

    const querySnapshot = await getDocs(q);

    const result = await Promise.all(querySnapshot.docs.map(async (doc) => {
      const docData = doc.data();
      const song = await getDocData('songs', docData.songId);
      if (!song) return null;
      const countComments = await countCommentsBySongId(docData.songId);
      const profile = await getDocData('profiles', song.ownerId);
      return {
        id: doc.id,
        ...docData,
        song: { ...song, countComments },
        profile,
      }
    }));
    return result.filter((e) => !!e);
  } catch (error) {
    console.error('Error fetching feed:', error);
    return [];
  }
}

export async function getAllProfilesData(start = null) {
  try {
    let q = query(
      collection(db, 'profiles'),
      orderBy('updatedAt', 'desc'),
      limit(12)
    );

    if (start) {
      q = query(q, startAfter(start));
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p => p.private === false);
  } catch (error) {
    console.error('Error fetching profiles:', error);
    return [];
  }
}

export async function getAllProfilesDataFollowed(followerIds = [], start = null) {
  try {
    let q = query(
      collection(db, 'profiles'),
      orderBy('updatedAt', 'desc'),
      limit(12)
    );

    if (start) {
      q = query(q, startAfter(start));
    }

    const querySnapshot = await getDocs(q);

    const filtered = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p =>
        p.private === false &&
        followerIds.includes(p.id)
      );

    return filtered;
  } catch (error) {
    console.error('Error fetching followed profiles:', error);
    return [];
  }
}

export async function getFollowingTargetIds(userId) {
  const targetIds = [];
  try {
    const groupsRef = collection(db, 'groups');
    const groupsQuery = query(groupsRef, where('followers', 'array-contains', userId));
    const groupsQuerySnapshot = await getDocs(groupsQuery);

    if (!groupsQuerySnapshot.empty) {
      groupsQuerySnapshot.forEach((doc) => {
        targetIds.push(doc.id);
      });
    }

    const profilesRef = collection(db, 'profiles');
    const profilesQuery = query(profilesRef, where('followers', 'array-contains', userId));
    const profilesQuerySnapshot = await getDocs(profilesQuery);

    if (!profilesQuerySnapshot.empty) {
      profilesQuerySnapshot.forEach((doc) => {
        targetIds.push(doc.id);
      });
    }
    return targetIds;
  } catch (error) {
    console.error('Error fetching feed:', error);
    return [];
  }
}

export async function getAllFollowingFeedData(targetIds = [], start = null) {
  try {
    if (!targetIds.length) {
      return [];
    }

    let q = query(
      collection(db, 'feed'),
      where('targetId', 'in', targetIds),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    if (start) {
      q = query(q, startAfter(start));
    }

    const querySnapshot = await getDocs(q);

    return await Promise.all(querySnapshot.docs.map(async (doc) => {
      const docData = doc.data();
      const song = await getDocData('songs', docData.songId);
      const countComments = await countCommentsBySongId(docData.songId);
      const profile = await getDocData('profiles', song.ownerId);
      return {
        id: doc.id,
        ...docData,
        song: { ...song, countComments },
        profile,
      }
    }));
  } catch (error) {
    console.error('Error fetching feed:', error);
    return [];
  }
}

/* Storage */
export function uploadFileWithProgress(filePath, file, onProgress) {
  return new Promise((resolve, reject) => {
    const fileRef = storageRef(storage, filePath);

    // Start the upload with resumable upload
    const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type });

    // Monitor progress
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Calculate progress as a percentage
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        // console.log(`Upload is ${progress}% done`);

        // Call the progress callback
        if (onProgress) {
          onProgress(progress, snapshot.state);
        }
      },
      (error) => {
        // Handle errors
        console.error("Upload failed:", error);
        reject(error);
      },
      async () => {
        // Handle successful upload
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        } catch (error) {
          console.error("Error getting download URL:", error);
          reject(error);
        }
      }
    );
  });
}

export async function deleteFolderFromStorage(folderPath) {
  const folderRef = storageRef(storage, folderPath);

  try {
    // List all files under the folder
    const listResult = await listAll(folderRef);

    if (listResult.items.length === 0) {
      console.log("No files found in the folder.");
      return;
    }

    // Delete all files
    const deletePromises = listResult.items.map((fileRef) => deleteObject(fileRef));
    await Promise.all(deletePromises);

    console.log(`Folder deleted successfully: ${folderPath}`);
  } catch (error) {
    console.error("Error deleting folder:", error);
    throw error;
  }
}

export function getPublicUrl(filePath) {
  const publicUrl = `https://firebasestorage.googleapis.com/v0/b/${BUCKET_NAME}/o/${encodeURIComponent(filePath)}?alt=media`;
  return publicUrl;
}

/* Admin */
export async function countDocuments(collectionName) {
  try {
    const q = query(collection(db, collectionName));
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error counting documents:", error);
    throw error;
  }
}

export async function getSongsWithSplitJobId() {
  try {
    const q = query(collection(db, "songs"), where("splitJobId", "!=", null));
    const querySnapshot = await getDocs(q);

    const songs = [];
    querySnapshot.forEach((doc) => {
      songs.push({ id: doc.id, ...doc.data() });
    });

    return songs;
  } catch (error) {
    console.error("Error get songs filterd by spiltJobId:", error);
    throw error;
  }
}

export async function getUserslist() {
  try {
    const users = await getAllDocs('users');
    const result = await Promise.all(
      users.map(async (user) => {
        const userData = await getDocData('profiles', user.uid);
        const sessionsData = await getUserSessions(user.uid);
        let songsData = [], songIds = [];
        sessionsData.forEach((session) => {
          songIds = [...songIds, ...session?.songs || []];
        });
        if (songIds.length) {
          songsData = await getUserSongs(songIds);
        }

        return { ...user, ...userData, sessionsData, songsData };
      })
    );

    const usersList = result.filter((e) => !!e);
    usersList.forEach((item) => item.totalSessions = item.sessionsData.filter((e) => !!e).length);
    usersList.forEach((item) => item.totalSongs = item.songsData.filter((e) => !!e).length);
    usersList.forEach((item) => item.totalSongSplits = item.songsData.filter((e) => !!e && !!e.splitJobId).length);

    return usersList;
  } catch (error) {
    console.error("Error get users list:", error);
    throw error;
  }
}

export async function deleteUserContent(userId) {
  try {
    const userData = await getDocData('users', userId);
    await Promise.all(
      (userData?.sessions || []).map(async (sessionId) => {
        const sessionData = await getDocData('sessions', sessionId);
        await Promise.all(
          (sessionData?.songs || []).map(async (songId) => {
            await deleteDocData('songs', songId);
            return await deleteFeedsWithSongId(songId);
          })
        );
        return await deleteDocData('sessions', sessionId);
      })
    );

    await deleteGroupsByAdmin(userId);
    // await updateDocData('users', userId, { deleted: true, sessions: [] });
    await deleteDocData('profiles', userId);
    await deleteDocData('users', userId);
    return await deleteUser(userId);
  } catch (error) {
    console.error("Error delete user's content:", error);
    throw error;
  }
}

export async function isBlockedUser(userId) {
  const userDoc = await getDocData('users', userId);
  return !!userDoc.blocked;
}

export async function isEmailBlocked(email) {
  const blockedDoc = await getDocData('blocks', email);
  if (blockedDoc) {
    await updateDocData('blocks', email, { attemps: (blockedDoc.attemps || 0) + 1 })
    return true;
  }
  return false;
}

export async function blockEmail(email) {
  try {
    await setDocData('blocks', email, { attemps: 0 });
    const existUser = await getDocByValue('users', 'email', email);
    if (existUser) {
      await revokeUser(existUser.uid);
      await updateDocData('users', existUser.uid, { blocked: true });
    }
    return existUser;
  } catch (error) {
    console.error("Error block email:", error);
    throw error;
  }
}

export async function unblockEmail(email) {
  try {
    await deleteDocData('blocks', email);
    const existUser = await getDocByValue('users', 'email', email);
    if (existUser) {
      await updateDocData('users', existUser.uid, { blocked: false });
    }
    return existUser;
  } catch (error) {
    console.error("Error unblock email:", error);
    throw error;
  }
}

/* Home */
export async function getTopProfiles(limitCount = 5) {
  try {
    const collectionRef = collection(db, 'profiles');
    const querySnapshot = await getDocs(query(collectionRef, where('private', '==', false)));

    const allProfiles = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Sort by followers.length then songs.length (ascending)
    const sortedProfiles = allProfiles.sort((a, b) => {
      const aFollowers = a.followers?.length || 0;
      const bFollowers = b.followers?.length || 0;
      const aSongs = a.songs?.length || 0;
      const bSongs = b.songs?.length || 0;

      if (aFollowers === bFollowers) {
        return bSongs - aSongs;
      }
      return bFollowers - aFollowers;
    });

    return sortedProfiles.slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching profiles:', error);
    return [];
  }
}
